import { useExperiment } from '@statsig/react-bindings';
import { useLocale } from 'next-intl';
import { createElement } from 'react';

import { useGeoContext } from '@/app/contexts/GeoProvider';

/**
 * Supported locales for Klarna messaging by country.
 * Each country can have multiple locale variations.
 */
const KLARNA_COUNTRY_LOCALES = {
  DE: ['de-DE', 'en-DE'],
  GB: ['en-GB'],
} as const;

type KlarnaSupportedCountry = keyof typeof KLARNA_COUNTRY_LOCALES;

/**
 * Determines the best Klarna locale for a given country and user locale.
 * Returns null if the country is not supported by Klarna.
 *
 * @param countryCode - ISO country code (e.g., 'DE', 'GB')
 * @param userLocale - User's current locale (e.g., 'en', 'de')
 * @returns Matched Klarna locale (e.g., 'en-GB') or null if country not supported
 */
function getBestLocaleForCountry(
  countryCode: string | undefined | null,
  userLocale: string,
): string | null {
  if (!countryCode || !(countryCode in KLARNA_COUNTRY_LOCALES)) {
    return null;
  }

  const supportedLocales =
    KLARNA_COUNTRY_LOCALES[countryCode as KlarnaSupportedCountry];

  // Match user locale to supported Klarna locale (e.g., 'en' → 'en-GB')
  const matchedLocale = supportedLocales.find((supportedLocale) =>
    supportedLocale.startsWith(userLocale),
  );

  // Use matched locale or default to first supported locale for the country
  return matchedLocale ?? supportedLocales[0];
}

interface KlarnaMessagingProps {
  readonly priceInCents: number;
}

export default function KlarnaMessaging({
  priceInCents,
}: KlarnaMessagingProps) {
  const locale = useLocale();
  const { countryData, isGeoReady } = useGeoContext();

  const showKlarnaMessaging = useExperiment('klarna_osm').get(
    'show_klarna_osm',
    false,
  );

  const klarnaLocale = getBestLocaleForCountry(
    countryData?.countryCode,
    locale,
  );

  if (!showKlarnaMessaging || !isGeoReady || !klarnaLocale) {
    return null;
  }

  return createElement('klarna-placement', {
    id: 'klarna-messaging-badge',
    className: 'pt-4',
    'data-key': 'credit-promotion-badge',
    'data-locale': klarnaLocale,
    'data-purchase-amount': priceInCents,
  });
}
